import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "react-toastify";

import MainContentHeader from "@components/MainContentHeader";
import * as commonS from "@styles/common";

import useStudentsImport from "./useStudentsImport";
import useCurrentProject from "@hooks/useCurrentProject";

import Table from "@components/Table";
import Modal from "@components/Modal";
import MoreActionsButton from "../../components/Table/MoreActionsButton";
import { Button, Icon, OutlinedInput } from "@mui/material";
import Select from "@mui/material/Select";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";

import {
  getGender,
  getKnowledgeLevel,
  getSpecialNeeds,
  getHelpWithStudy,
} from "@helpers/getGermanNaming";

import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";
import * as S from "./styles";
import studentsApi from "@api/students.api";
import classesApi from "@api/classes.api";

import StudentHint from "@components/Hints/StudentHint";
import COLORS from "@styles/theme/colors";

import stores from "@stores";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const StudentsView = ({ project }) => {
  const {
    studentsStore,
    layoutStore: { setConfirmModal },
  } = stores;
  const { students } = studentsStore;
  const [emptyValuesArr, setEmptyValuesArr] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [address, setAddress] = useState(null);
  const [student, setStudent] = useState({});
  const [teachers, setTeachers] = useState(new Set());
  const [currentStudent, setCurrentStudent] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { currentProject } = useCurrentProject();

  const isEmptyValue = (obj, key) => {
    if (!obj[key] || obj[key] === " ") return true;
    return false;
  };

  const errorArr = students
    .filter(
      (elem) =>
        isEmptyValue(elem, "fullName") ||
        isEmptyValue(elem, "address") ||
        isEmptyValue(elem, "knowledgeLevel") ||
        isEmptyValue(elem, "gender")
    )
    .map((item) => item._id);

  const onImportCompleted = async () => {
    await studentsStore.fetchStudentsList(project._id);
  };

  const { openImport, closeImport, isImportActive, ImportView } =
    useStudentsImport({
      projectId: project._id,
      onImportCompleted,
    });

  useEffect(() => {
    const getStudentsList = async () => {
      await studentsStore.fetchStudentsList(project._id);
      const classes = await classesApi.fetch(project._id);
      const classTeachers = new Set();
      classes.forEach((e) => classTeachers.add(e.teacherName));
      setTeachers(classTeachers);
    };
    getStudentsList();
  }, []);

  useEffect(() => {
    if (address) {
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) =>
          setStudent({
            ...student,
            address: address?.label,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            placeId: address?.value?.place_id,
          })
        );
    }
  }, [address?.label]);

  const onDeleteStudent = (id: string) => {
    setConfirmModal({
      type: "warning",
      open: true,
      title: "Element wirklich löschen?",
      positiveCallback: async () => {
        await studentsApi.delete(id).then((e: any) =>
          toast.success("SuS erfolgreich gelöscht’", { autoClose: 3000 })
        );
        await studentsStore.fetchStudentsList(project._id);
      },
      positiveButtonText: "Ok",
    });
  };

  const onDeleteAll = async () => {
    setConfirmModal({
      type: "warning",
      open: true,
      title: "Are you sure you want to remove all items ?",
      positiveCallback: async () => {
        for (const student of students) {
          await studentsApi.delete(student._id);
          await studentsStore.fetchStudentsList(project._id);
        }
      },
      positiveButtonText: "Ok",
    });
  };

  if (!students) {
    return <div>Loading....</div>;
  }

  const headCells = [
    { title: "#", key: "number", sortable: false, width: "4%" },
    { title: "Name", key: "fullName", sortable: false, width: "135px" },
    { title: "Adresse", key: "address", sortable: false, width: "200px" },
    { title: "Geschlecht", key: "gender", sortable: false },
    { title: "Letzte Lehrperson", key: "teacher", sortable: false },
    { title: "Lehrperson", key: "teacherName", sortable: false },
    { title: "Freunde", key: "bestFriendName" },
    { title: "Lernbegleitung SHP", key: "helpWithStudy" },
    { title: "Anforderungen", key: "knowledgeLevel" },
    {
      title: "Fördermassnahmen",
      key: "specialNeeds",
      sortable: false,
    },
    { title: "Aktionen", key: "actions" },
  ];

  const handleOpenEditModal = (row: any) => {
    setIsEdit(true);
    setModalOpen(true);
    setCurrentStudent(row);
  };

  const handleEdit = async () => {
    setEmptyValuesArr(errorArr);
    await studentsApi
      .edit({
        ...currentStudent,
        fullName: `${currentStudent?.lastName} ${currentStudent?.firstName}`,
        id: currentStudent._id,
      })
      .then((e: any) =>
        toast.success('Kid was edited successfully', { autoClose: 3000 })
      );
    await studentsStore.fetchStudentsList(project._id);
    setModalOpen(false);
    setIsEdit(false);
  };

  useEffect(() => {
    if (students.length) {
      setEmptyValuesArr(errorArr);
    }
  }, [students]);

  const handleEditAddress = (e: any, row) => {
    setEmptyValuesArr(errorArr);
    geocodeByAddress(e?.label)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) =>
        setCurrentStudent({
          ...currentStudent,
          address: e.label,
          placeId: e.value.place_id,
          location: {
            type: "Point",
            coordinates: [lng, lat],
          },
        })
      );
  };

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    const { name, value } = target;
    if (isEdit) {
      if (name === "specialNeeds" || name === "helpWithStudy") {
        setCurrentStudent({
          ...currentStudent,
          [name]: typeof value === "string" ? value.split(",") : value,
        });
      } else {
        setCurrentStudent({
          ...currentStudent,
          [name]: value,
        });
      }
    } else {
      if (name === "specialNeeds" || name === "helpWithStudy") {
        setStudent({
          ...student,
          [name]: typeof value === "string" ? value.split(",") : value,
        });
      } else {
        setStudent({
          ...student,
          [name]: value,
        });
      }
    }
  };

  useEffect(() => {
    if (
      student?.firstName &&
      student?.lastName &&
      student?.address &&
      student?.knowledgeLevel &&
      student?.gender
    ) {
      setButtonDisabled(false);
    }
  }, [student]);

  const handleAddNew = async () => {
    setModalOpen(false);
    await studentsApi
      .add({
        ...student,
        fullName: `${student?.lastName} ${student?.firstName}`,
        project: currentProject._id,
      })
      .then((e: any) =>
        toast.success('SuS erfolgreich hinzugefügt', { autoClose: 3000 })
      );
    await setStudent({});
    await setAddress(null);
    await studentsStore.fetchStudentsList(project._id);
    setButtonDisabled(true);
  };

  const getFormattedRows = () =>
    students.length === 0
      ? []
      : students
          .sort((a, b) => {
            if (a.lastName && b.lastName) {
              return a.lastName.localeCompare(b.lastName);
            } else {
              return a.fullName.localeCompare(b.fullName);
            }
          })
          .map((row: any, index: number) => {
            return {
              id: row?._id,
              number: index + 1,
              fullName: (
                <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <S.TableLabel
                    className={isEmptyValue(row, "fullName") && "error"}
                  >
                    {isEmptyValue(row, "fullName") ? (
                      <span onClick={() => handleOpenEditModal(row)}>
                        Please edit and enter full name
                      </span>
                    ) : (
                      <strong onClick={() => handleOpenEditModal(row)}>
                        {row?.lastName} {row?.firstName}
                      </strong>
                    )}
                  </S.TableLabel>
                  {row?.note && (
                    <svg
                      width="16"
                      height="16"
                      style={{ marginLeft: 5 }}
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.69618 8.03551L4 8.56414L4.52857 5.86791L9.9476 0.448923C10.5462 -0.149642 11.5166 -0.149641 12.1152 0.448924C12.7138 1.04749 12.7138 2.01795 12.1152 2.61652L6.69618 8.03551Z"
                        fill="#778097"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.25 2.25H5.74609C6.09127 2.25 6.37109 1.97018 6.37109 1.625C6.37109 1.27982 6.09127 1 5.74609 1H1.09375C0.803669 1 0.52547 1.11523 0.320352 1.32035C0.115234 1.52547 0 1.80367 0 2.09375V11.4688C0 11.7588 0.115234 12.037 0.320352 12.2421C0.525471 12.4473 0.803671 12.5625 1.09375 12.5625H10.4688C10.7588 12.5625 11.037 12.4473 11.2421 12.2421C11.4473 12.037 11.5625 11.7588 11.5625 11.4688V6.81641C11.5625 6.47123 11.2827 6.19141 10.9375 6.19141C10.5923 6.19141 10.3125 6.47123 10.3125 6.81641V11.3125H1.25V2.25Z"
                        fill="#778097"
                      />
                    </svg>
                  )}
                </div>
              ),
              address: (
                <S.TableLabel
                  className={isEmptyValue(row, "address") && "error"}
                  onClick={() => handleOpenEditModal(row)}
                >
                  {isEmptyValue(row, "address")
                    ? "Please edit and enter address"
                    : row?.address}
                </S.TableLabel>
              ),
              gender: (
                <S.TableLabel
                  className={isEmptyValue(row, "gender") && "error"}
                  onClick={() => handleOpenEditModal(row)}
                >
                  {isEmptyValue(row, "gender")
                    ? "Please edit and enter gender"
                    : getGender(row?.gender)}
                </S.TableLabel>
              ),
              teacher: row.teacher,
              teacherName: (
                <S.TableLabel
                  className={
                    !!teachers.size && !teachers.has(row.teacherName)
                      ? "error"
                      : ""
                  }
                  onClick={() => handleOpenEditModal(row)}
                >
                  {row?.teacherName}
                </S.TableLabel>
              ),
              bestFriendName: row.bestFriendName,
              helpWithStudy: (
                <p style={{ display: "flex", gap: 5 }}>
                  {row.helpWithStudy.map((value) => (
                    <span>{getHelpWithStudy(value)}</span>
                  ))}
                </p>
              ),
              knowledgeLevel: <S.TableLabel
              className={isEmptyValue(row, "knowledgeLevel") && "error"}
              onClick={() => handleOpenEditModal(row)}
            >
              {isEmptyValue(row, "knowledgeLevel")
                ? "Please edit and enter Anforderungen"
                : getKnowledgeLevel(row?.knowledgeLevel)}
            </S.TableLabel>,
              specialNeeds: (
                <p style={{ display: "flex", gap: 5 }}>
                  {row.specialNeeds.map((value) => (
                    <span>{getSpecialNeeds(value)}</span>
                  ))}
                </p>
              ),
              actions: (
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <MoreActionsButton
                    onDelete={() => onDeleteStudent(row._id, row.fullName)}
                    isEdit
                    onEdit={() => handleOpenEditModal(row)}
                  />
                  <S.InfoWrap
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget);
                      setCurrentStudent(row);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#4F77DE"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-.001 5.75c.69 0 1.251.56 1.251 1.25s-.561 1.25-1.251 1.25-1.249-.56-1.249-1.25.559-1.25 1.249-1.25zm2.001 12.25h-4v-1c.484-.179 1-.201 1-.735v-4.467c0-.534-.516-.618-1-.797v-1h3v6.265c0 .535.517.558 1 .735v.999z" />
                    </svg>
                  </S.InfoWrap>
                </div>
              ),
            };
          });
  return (
    <commonS.MainBody>
      <MainContentHeader
        title={project.title}
        buttonTitle={isImportActive ? "Import abbrechen" : "Importieren"}
        onButtonClick={isImportActive ? closeImport : openImport}
      />
      <div style={{ marginBottom: isImportActive ? 20 : 0 }}>{ImportView}</div>

      {students?.length === 0 ? (
        <h2>Es wurden keine SuS hinzugefügt oder importiert</h2>
      ) : (
        <>
          <Table
            headRows={headCells}
            bodyRows={getFormattedRows()}
            errorRowIds={emptyValuesArr}
            isSus
            sorting={{
              sort: "",
              onChangeSorting: () => alert("SortChange"),
            }}
          />
          <S.TableFooter>
            <div>
              <Button variant="text" onClick={() => setModalOpen(true)}>
                + Neue Hinzufügen
              </Button>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <Button variant="text" onClick={onDeleteAll}>
                Alles löschen
              </Button>
            </div>
          </S.TableFooter>

          <Modal
            modalOpen={isModalOpen}
            isEdit={isEdit}
            onClose={() => {
              setModalOpen(false);
              setIsEdit(false);
            }}
            disabled={isEdit ? false : isButtonDisabled}
            onConfirm={isEdit ? handleEdit : handleAddNew}
            setModalOpen={setModalOpen}
            title={isEdit ? "SuS bearbeiten" : "Neue Hinzufügen"}
          >
            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="firstName" shrink={false}>
                  Vorname<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={
                    (isEdit && currentStudent.firstName) || student.firstName
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="firstName"
                  id="firstName"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="lastName" shrink={false}>
                  Name<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={
                    (isEdit && currentStudent.lastName) || student.lastName
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="lastName"
                  id="lastName"
                />
              </S.FormItem>
            </S.FormRow>
            <S.FormRow>
              {isEdit ? (
                <S.FormItem>
                  <InputLabel htmlFor="address" shrink={false}>
                    Adresse<span style={{ color: COLORS.red }}>*</span>
                  </InputLabel>
                  <GooglePlacesAutocomplete
                    apiOptions={{ language: "de", region: "ch" }}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ["ch"],
                      },
                    }}
                    selectProps={{
                      value: !isEmptyValue(currentStudent, "address")
                        ? {
                            label: currentStudent?.address,
                            value: {
                              place_id: currentStudent?.placeId,
                            },
                          }
                        : {
                            label: "Plese enter address",
                            value: {
                              place_id: null,
                            },
                          },
                      onChange: (e) => handleEditAddress(e, currentStudent),
                      styles: {
                        input: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: state.isFocused
                            ? "#4F77DE"
                            : "rgba(102, 102, 102, 0.35)",
                          outline: "none",
                          boxShadow: "none",
                          borderRadius: 8,
                          padding: "2.5px 5px",
                        }),
                        option: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        singleValue: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                      },
                    }}
                  />
                </S.FormItem>
              ) : (
                <S.FormItem>
                  <InputLabel htmlFor="address" shrink={false}>
                    Adresse<span style={{ color: COLORS.red }}>*</span>
                  </InputLabel>
                  <GooglePlacesAutocomplete
                    apiOptions={{ language: "de", region: "ch" }}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ["ch"],
                      },
                    }}
                    selectProps={{
                      value: address,
                      onChange: setAddress,
                      styles: {
                        input: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: state.isFocused
                            ? "#4F77DE"
                            : "rgba(102, 102, 102, 0.35)",
                          outline: "none",
                          boxShadow: "none",
                          borderRadius: 8,
                          padding: "2.5px 5px",
                        }),
                        option: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                        singleValue: (provided: any) => ({
                          ...provided,
                          color: "#778097",
                        }),
                      },
                    }}
                  />
                </S.FormItem>
              )}

              <S.FormItem>
                <InputLabel htmlFor="susId" shrink={false}>
                  SuS ID
                </InputLabel>
                <TextField
                  id="susId"
                  onChange={handleChange}
                  value={(isEdit && currentStudent.susId) || student.susId}
                  name="susId"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="genderSelect" shrink={false}>
                  Geschlecht<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <Select
                  labelId="genderSelect"
                  id="gender"
                  label="genderSelect"
                  name="gender"
                  value={(isEdit && currentStudent.gender) || student.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="male">Männlich</MenuItem>
                  <MenuItem value="female">Weiblich</MenuItem>
                </Select>
              </S.FormItem>
            </S.FormRow>
            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="teacher" shrink={false}>
                  Letzte Lehrperson
                </InputLabel>
                <TextField
                  value={(isEdit && currentStudent.teacher) || student.teacher}
                  onChange={handleChange}
                  variant="outlined"
                  name="teacher"
                  id="teacher"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="teacherName" shrink={false}>
                  Lehrperson
                </InputLabel>
                <Select
                  labelId="teacherName"
                  id="teacherName"
                  label="teacherName"
                  name="teacherName"
                  value={
                    (isEdit && currentStudent.teacherName) ||
                    student.teacherName
                  }
                  onChange={handleChange}
                  name="teacherName"
                  variant="outlined"
                >
                  <MenuItem value=" ">Nein</MenuItem>
                  {teachers.size &&
                    [...teachers].map((e) => (
                      <MenuItem value={e}>{e}</MenuItem>
                    ))}
                </Select>
              </S.FormItem>
            </S.FormRow>
            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="specialNote" shrink={false}>
                  Stärken / Ressourcen
                </InputLabel>
                <TextField
                  id="specialNote"
                  onChange={handleChange}
                  value={
                    (isEdit && currentStudent.specialNote) ||
                    student.specialNote
                  }
                  name="specialNote"
                  variant="outlined"
                />
              </S.FormItem>
              <S.FormItem>
                <InputLabel htmlFor="note" shrink={false}>
                  Bemerkungen
                </InputLabel>
                <TextField
                  id="note"
                  onChange={handleChange}
                  value={(isEdit && currentStudent.note) || student.note}
                  name="note"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>
            <S.FormItem>
              <InputLabel htmlFor="germanLanguageLevel" shrink={false}>
                Deutschkenntnisse der Eltern
              </InputLabel>
              <Select
                labelId="germanLanguageLevel"
                id="germanLanguageLevel"
                label="germanLanguageLevel"
                name="germanLanguageLevel"
                value={
                  (isEdit && currentStudent.germanLanguageLevel) ||
                  student.germanLanguageLevel
                }
                onChange={handleChange}
              >
                <MenuItem value="little">Genügend bis gut</MenuItem>
                <MenuItem value="high">Übersetzung notwendig</MenuItem>
              </Select>
            </S.FormItem>

            <S.FormItem>
              <InputLabel htmlFor="bestFriendName" shrink={false}>
                Freunde
              </InputLabel>
              <TextField
                id="bestFriendName"
                onChange={handleChange}
                value={
                  (isEdit && currentStudent.bestFriendName) ||
                  student.bestFriendName
                }
                name="bestFriendName"
                variant="outlined"
              />
            </S.FormItem>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevel" shrink={false}>
                  Anforderungen<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <Select
                  labelId="knowledgeLevel"
                  id="knowledgeLevel"
                  label="knowledgeLevel"
                  name="knowledgeLevel"
                  value={
                    (isEdit && currentStudent.knowledgeLevel) ||
                    student.knowledgeLevel
                  }
                  onChange={handleChange}
                >
                  <MenuItem value="basic">Teilweise erfüllt</MenuItem>
                  <MenuItem value="intermediate">Erfüllt</MenuItem>
                  <MenuItem value="advanced">Teilweise übertroffen</MenuItem>
                </Select>
              </S.FormItem>
              <S.FormItem>
                <InputLabel htmlFor="specialNeeds" shrink={false}>
                  Fördermassnahmen
                </InputLabel>
                <Select
                  labelId="specialNeeds"
                  id="specialNeeds"
                  multiple
                  name="specialNeeds"
                  value={
                    (isEdit && currentStudent.specialNeeds) ||
                    student.specialNeeds ||
                    []
                  }
                  onChange={handleChange}
                  input={<OutlinedInput label="Fördermassnahmen" />}
                  renderValue={(selected) => selected.map(item => getSpecialNeeds(item)).join(", ")}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="germanassecondlanguage">
                    <Checkbox
                      checked={
                        isEdit
                          ? currentStudent.specialNeeds?.indexOf(
                              "germanassecondlanguage"
                            ) > -1
                          : student.specialNeeds?.indexOf(
                              "germanassecondlanguage"
                            ) > -1
                      }
                    />
                    <ListItemText primary="DaZ" />
                  </MenuItem>
                  <MenuItem value="logopedic">
                    <Checkbox
                      checked={
                        isEdit
                          ? currentStudent.specialNeeds?.indexOf("logopedic") >
                            -1
                          : student.specialNeeds?.indexOf("logopedic") > -1
                      }
                    />
                    <ListItemText primary="Logopädie" />
                  </MenuItem>
                  <MenuItem value="psychomotor">
                    <Checkbox
                      checked={
                        isEdit
                          ? currentStudent.specialNeeds?.indexOf(
                              "psychomotor"
                            ) > -1
                          : student.specialNeeds?.indexOf("psychomotor") > -1
                      }
                    />
                    <ListItemText primary="Psychomotorik" />
                  </MenuItem>
                </Select>
              </S.FormItem>
            </S.FormRow>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevelNote" shrink={false}>
                  Anforderungen wurde abgeklärt (SPL, Arzt)
                </InputLabel>
                <TextField
                  id="knowledgeLevelNote"
                  onChange={handleChange}
                  value={
                    (isEdit && currentStudent.knowledgeLevelNote) ||
                    student.knowledgeLevelNote
                  }
                  name="knowledgeLevelNote"
                  variant="outlined"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="knowledgeLevelSpecialNote" shrink={false}>
                  Anforderungen Grund
                </InputLabel>
                <TextField
                  id="knowledgeLevelSpecialNote"
                  onChange={handleChange}
                  value={
                    (isEdit && currentStudent.knowledgeLevelSpecialNote) ||
                    student.knowledgeLevelSpecialNote
                  }
                  name="knowledgeLevelSpecialNote"
                  variant="outlined"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="inS" shrink={false}>
                  InS
                </InputLabel>
                <Select
                  labelId="inS"
                  id="inS"
                  label="inS"
                  name="inS"
                  value={(isEdit && currentStudent.inS) || student.inS}
                  onChange={handleChange}
                >
                  <MenuItem value=" ">Empty</MenuItem>
                  <MenuItem value="ja">Ja</MenuItem>
                  <MenuItem value="nein">Nein</MenuItem>
                </Select>
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="specialNeedsNote" shrink={false}>
                  Fördermassnahmen Weitere
                </InputLabel>
                <TextField
                  value={
                    (isEdit && currentStudent.specialNeedsNote) ||
                    student.specialNeedsNote
                  }
                  onChange={handleChange}
                  variant="outlined"
                  name="specialNeedsNote"
                  id="specialNeedsNote"
                />
              </S.FormItem>
            </S.FormRow>

            <S.FormItem>
              <InputLabel htmlFor="helpWithStudy" shrink={false}>
                Lernbegleitung SHP
              </InputLabel>
              <Select
                labelId="helpWithStudy"
                id="helpWithStudy"
                name="helpWithStudy"
                multiple
                value={
                  (isEdit && currentStudent?.helpWithStudy) ||
                  student.helpWithStudy ||
                  []
                }
                onChange={handleChange}
                input={<OutlinedInput label="Lernbegleitung SHP" />}
                renderValue={(selected) => selected.map(item => getHelpWithStudy(item)).join(", ")}
                MenuProps={MenuProps}
              >
                <MenuItem value="german">
                  <Checkbox
                    checked={
                      isEdit
                        ? currentStudent.helpWithStudy?.indexOf("german") > -1
                        : student.helpWithStudy?.indexOf("german") > -1
                    }
                  />
                  <ListItemText primary="Deutsch" />
                </MenuItem>
                <MenuItem value="maths">
                  <Checkbox
                    checked={
                      isEdit
                        ? currentStudent.helpWithStudy?.indexOf("maths") > -1
                        : student.helpWithStudy?.indexOf("maths") > -1
                    }
                  />
                  <ListItemText primary="Mathe" />
                </MenuItem>
                <MenuItem value="lzaGerman">
                  <Checkbox
                    checked={
                      isEdit
                        ? currentStudent.helpWithStudy?.indexOf("lzaGerman") >
                          -1
                        : student.helpWithStudy?.indexOf("lzaGerman") > -1
                    }
                  />
                  <ListItemText primary="LZA Deutsch" />
                </MenuItem>
                <MenuItem value="lzaMaths">
                  <Checkbox
                    checked={
                      isEdit
                        ? currentStudent.helpWithStudy?.indexOf("lzaMaths") > -1
                        : student.helpWithStudy?.indexOf("lzaMaths") > -1
                    }
                  />
                  <ListItemText primary="LZA Mathe" />
                </MenuItem>
              </Select>
            </S.FormItem>
          </Modal>

          {!!currentStudent && (
            <StudentHint
              student={currentStudent}
              handleClose={() => setAnchorEl(null)}
              anchorEl={anchorEl}
            />
          )}
        </>
      )}
    </commonS.MainBody>
  );
};

export default observer(StudentsView);
